import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Перехватчик для обработки 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 на самом /login — это штатный ответ на неверный пароль,
    // а не истёкшая сессия. Редиректить нельзя: перезагрузка страницы
    // стирает сообщение об ошибке.
    const isLoginRequest = error.config?.url === '/login';
    if (error.response?.status === 401 && !isLoginRequest) {
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Аутентификация
export const login = async (username, password) => {
  const response = await api.post('/login', { username, password });
  return response.data;
};

export const logout = async () => {
  const response = await api.post('/logout');
  return response.data;
};

export const getCurrentUser = async () => {
  const response = await api.get('/me');
  return response.data;
};

// Организация текущего поддомена — нужно ещё до входа (брендинг логина,
// сообщение «организация не найдена» / «подписка неактивна»)
// Восстановление пароля. Заявка отвечает 200 даже для несуществующего
// пользователя — так эндпоинт не превращается в проверялку чужих логинов,
// поэтому по ответу нельзя (и не нужно) понять, найден ли пользователь.
export const requestPasswordReset = async (login) => {
  const response = await api.post('/password-reset/request', { login });
  return response.data;
};

export const confirmPasswordReset = async (token, password) => {
  const response = await api.post('/password-reset/confirm', { token, password });
  return response.data;
};

// --- Двухфакторная аутентификация ---
export const getTwoFactorStatus = async () => {
  const response = await api.get('/2fa/status');
  return response.data;
};

// Пароль спрашивается заново намеренно: см. комментарий в TwoFactorCard.
export const setupTwoFactor = async (password) => {
  const response = await api.post('/2fa/setup', { password });
  return response.data;
};

export const enableTwoFactor = async (code) => {
  const response = await api.post('/2fa/enable', { code });
  return response.data;
};

export const disableTwoFactor = async (password, code) => {
  const response = await api.post('/2fa/disable', { password, code });
  return response.data;
};

// Второй шаг входа. Первый (login) при включённой 2FA возвращает
// success:false и totp_required:true, сессии на этот момент ещё нет.
export const loginTwoFactor = async (code) => {
  const response = await api.post('/login/2fa', { code });
  return response.data;
};

export const getOrgInfo = async () => {
  const response = await api.get('/org/info');
  return response.data;
};

// === Платформенная админка (только на поддомене PLATFORM_ADMIN_SUBDOMAIN) ===
export const getPlatformMetrics = async () => {
  const response = await api.get('/platform/metrics');
  return response.data;
};

export const getPlatformOrganizations = async () => {
  const response = await api.get('/platform/organizations');
  return response.data;
};

export const createPlatformOrganization = async (data) => {
  const response = await api.post('/platform/organizations', data);
  return response.data;
};

export const updatePlatformOrganization = async (orgId, data) => {
  const response = await api.patch(`/platform/organizations/${orgId}`, data);
  return response.data;
};

export const getPlatformOrganization = async (orgId) => {
  const response = await api.get(`/platform/organizations/${orgId}`);
  return response.data;
};

// Список «требует внимания»: провалившиеся списания, кончающиеся триалы,
// тишина в аккаунтах, неотвеченные тикеты. Правила считает сервер.
export const getPlatformAttention = async () => {
  const response = await api.get('/platform/attention');
  return response.data;
};

export const getPlatformPayments = async ({ status, orgId } = {}) => {
  const response = await api.get('/platform/payments', {
    params: { ...(status ? { status } : {}), ...(orgId ? { org_id: orgId } : {}) },
  });
  return response.data;
};

// Счета на оплату по безналу — лента по всей платформе + ручное подтверждение
export const getPlatformInvoices = async ({ status, orgId } = {}) => {
  const response = await api.get('/platform/invoices', {
    params: { ...(status ? { status } : {}), ...(orgId ? { org_id: orgId } : {}) },
  });
  return response.data;
};

export const markPlatformInvoicePaid = async (invoiceId) => {
  const response = await api.post(`/platform/invoices/${invoiceId}/mark-paid`);
  return response.data;
};

// Промокоды
export const getPlatformPromoCodes = async () => {
  const response = await api.get('/platform/promo-codes');
  return response.data;
};

export const createPlatformPromoCode = async (data) => {
  const response = await api.post('/platform/promo-codes', data);
  return response.data;
};

export const updatePlatformPromoCode = async (promoId, data) => {
  const response = await api.patch(`/platform/promo-codes/${promoId}`, data);
  return response.data;
};

// Отключение разделов: org_id = 0 — у всех организаций
export const getPlatformModules = async () => {
  const response = await api.get('/platform/modules');
  return response.data;
};

export const disablePlatformModule = async (orgId, page, reason) => {
  const response = await api.post('/platform/modules', { org_id: orgId, page, reason });
  return response.data;
};

export const enablePlatformModule = async (orgId, page) => {
  const response = await api.delete('/platform/modules', { data: { org_id: orgId, page } });
  return response.data;
};

// Воронка лендинг -> триал -> оплата
export const getPlatformFunnel = async (days = 30) => {
  const response = await api.get('/platform/funnel', { params: { days } });
  return response.data;
};

// Когортная конверсия и churn
export const getPlatformCohorts = async () => {
  const response = await api.get('/platform/cohorts');
  return response.data;
};

// Лиды, не дошедшие до конца регистрации (ранний захват email на лендинге)
export const getPlatformLeads = async () => {
  const response = await api.get('/platform/leads');
  return response.data;
};

// Возвращает одноразовую ссылку на поддомен организации — сессия режима
// просмотра заводится уже там (кука платформы на чужой поддомен не летит).
export const impersonateOrganization = async (orgId, userId) => {
  const response = await api.post(`/platform/organizations/${orgId}/impersonate`,
    userId ? { user_id: userId } : {});
  return response.data;
};

export const endImpersonation = async () => {
  const response = await api.post('/impersonation/end');
  return response.data;
};

export const getPlatformPlans = async () => {
  const response = await api.get('/platform/plans');
  return response.data;
};

export const createPlatformPlan = async (data) => {
  const response = await api.post('/platform/plans', data);
  return response.data;
};

export const updatePlatformPlan = async (planId, data) => {
  const response = await api.patch(`/platform/plans/${planId}`, data);
  return response.data;
};

// Тикеты поддержки всех организаций — супер-админ отвечает отсюда
export const getPlatformSupportTickets = async (params = {}) => {
  const response = await api.get('/platform/support/tickets', { params });
  return response.data;
};

export const getPlatformSupportTicket = async (ticketId) => {
  const response = await api.get(`/platform/support/tickets/${ticketId}`);
  return response.data;
};

export const replyPlatformSupportTicket = async (ticketId, body, attachment) => {
  const response = await api.post(`/platform/support/tickets/${ticketId}/messages`, { body, ...attachment });
  return response.data;
};

export const getPlatformSupportAttachment = async (ticketId, messageId) => {
  const response = await api.get(`/platform/support/tickets/${ticketId}/messages/${messageId}/attachment`);
  return response.data;
};

export const updatePlatformSupportTicket = async (ticketId, updates) => {
  const response = await api.patch(`/platform/support/tickets/${ticketId}`, updates);
  return response.data;
};

// === Техподдержка (обращения организации) ===
export const getSupportTickets = async () => {
  const response = await api.get('/support/tickets');
  return response.data;
};

export const createSupportTicket = async (subject, body, options = {}) => {
  const response = await api.post('/support/tickets', { subject, body, ...options });
  return response.data;
};

export const getSupportTicket = async (ticketId) => {
  const response = await api.get(`/support/tickets/${ticketId}`);
  return response.data;
};

export const replySupportTicket = async (ticketId, body, attachment) => {
  const response = await api.post(`/support/tickets/${ticketId}/messages`, { body, ...attachment });
  return response.data;
};

export const getSupportAttachment = async (ticketId, messageId) => {
  const response = await api.get(`/support/tickets/${ticketId}/messages/${messageId}/attachment`);
  return response.data;
};

// === Биллинг (оплата подписки организацией, роль admin) ===
export const getBillingStatus = async () => {
  const response = await api.get('/billing/status');
  return response.data;
};

export const getBillingPlans = async () => {
  const response = await api.get('/billing/plans');
  return response.data;
};

export const getBillingPayments = async () => {
  const response = await api.get('/billing/payments');
  return response.data;
};

export const billingCheckout = async (planId, promoCode) => {
  const response = await api.post('/billing/checkout', { plan_id: planId, promo_code: promoCode || undefined });
  return response.data;
};

export const billingCancel = async () => {
  const response = await api.post('/billing/cancel');
  return response.data;
};

// Скидка на следующее списание вместо отмены (retention-предложение)
export const applyRetentionOffer = async () => {
  const response = await api.post('/billing/retention-offer');
  return response.data;
};

// Реквизиты организации и счета на оплату по безналу
export const getBillingRequisites = async () => {
  const response = await api.get('/billing/requisites');
  return response.data;
};

export const saveBillingRequisites = async (data) => {
  const response = await api.put('/billing/requisites', data);
  return response.data;
};

export const createBillingInvoice = async (planId, promoCode) => {
  const response = await api.post('/billing/invoice', { plan_id: planId, promo_code: promoCode || undefined });
  return response.data;
};

export const getBillingInvoices = async () => {
  const response = await api.get('/billing/invoices');
  return response.data;
};

// Самостоятельное подключение iiko администратором организации
export const updateOrgIikoCredentials = async (data) => {
  const response = await api.put('/org/iiko-credentials', data);
  return response.data;
};

// Telegram-бот для доставки персональных предложений (CRM)
export const getOrgTelegramBot = async () => {
  const response = await api.get('/org/telegram-bot');
  return response.data;
};

export const updateOrgTelegramBot = async (botToken) => {
  const response = await api.put('/org/telegram-bot', { bot_token: botToken });
  return response.data;
};

// Период аналитики: число дней (готовые окна) или { from, to } — произвольный
const periodParams = (period) => {
  if (period && typeof period === 'object') return { from: period.from, to: period.to };
  return period ? { days: period } : {};
};

// Лояльность
export const getLoyalty = async (period) => {
  const response = await api.get('/loyalty', { params: periodParams(period) });
  return response.data;
};

export const refreshLoyalty = async () => {
  const response = await api.get('/loyalty/refresh');
  return response.data;
};

export const getFirstVisit = async () => {
  const response = await api.get('/loyalty/first-visit');
  return response.data;
};

export const refreshFirstVisit = async () => {
  const response = await api.get('/loyalty/first-visit/refresh');
  return response.data;
};

// Поиск гостя по телефону через Cloud API (только admin/manager)
export const lookupGuest = async (phone) => {
  const response = await api.post('/loyalty/guest-lookup', { phone });
  return response.data;
};

// База гостей (только admin/manager)
export const getGuests = async (params = {}) => {
  const response = await api.get('/loyalty/guests', { params });
  return response.data;
};

// Выгрузка идёт обычной ссылкой, чтобы браузер сам сохранил файл
export const guestsExportUrl = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return `/api/loyalty/guests/export${q ? `?${q}` : ''}`;
};

// === CRM ===
export const getCrmGuests = async (params = {}) => {
  const response = await api.get('/crm/guests', { params });
  return response.data;
};

export const crmGuestsExportUrl = (params = {}) => {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== '' && v != null)
  ).toString();
  return `/api/crm/guests/export${q ? `?${q}` : ''}`;
};

export const resolveCrmGuest = async (data) => {
  const response = await api.post('/crm/guests/resolve', data);
  return response.data;
};

export const getCrmGuestDetail = async (guestId) => {
  const response = await api.get(`/crm/guests/${guestId}`);
  return response.data;
};

export const addCrmNote = async (guestId, text) => {
  const response = await api.post(`/crm/guests/${guestId}/notes`, { text });
  return response.data;
};

export const setCrmTags = async (guestId, tags) => {
  const response = await api.put(`/crm/guests/${guestId}/tags`, { tags });
  return response.data;
};

export const getCrmTags = async () => {
  const response = await api.get('/crm/tags');
  return response.data;
};

export const getCrmTasks = async (params = {}) => {
  const response = await api.get('/crm/tasks', { params });
  return response.data;
};

export const createCrmTask = async (data) => {
  const response = await api.post('/crm/tasks', data);
  return response.data;
};

export const updateCrmTask = async (taskId, data) => {
  const response = await api.put(`/crm/tasks/${taskId}`, data);
  return response.data;
};

export const deleteCrmTask = async (taskId) => {
  const response = await api.delete(`/crm/tasks/${taskId}`);
  return response.data;
};

export const bulkTagCrmGuests = async (guestIds, tag) => {
  const response = await api.post('/crm/guests/bulk-tag', { guest_ids: guestIds, tag });
  return response.data;
};

export const bulkCreateCrmTasks = async (guestIds, data) => {
  const response = await api.post('/crm/guests/bulk-task', { guest_ids: guestIds, ...data });
  return response.data;
};

export const getCrmSegments = async () => {
  const response = await api.get('/crm/segments');
  return response.data;
};

export const createCrmSegment = async (name, filters) => {
  const response = await api.post('/crm/segments', { name, filters });
  return response.data;
};

export const deleteCrmSegment = async (segmentId) => {
  const response = await api.delete(`/crm/segments/${segmentId}`);
  return response.data;
};

export const getCrmGuestAnalytics = async () => {
  const response = await api.get('/crm/analytics/guests');
  return response.data;
};

export const getCrmEngagementAnalytics = async () => {
  const response = await api.get('/crm/analytics/engagement');
  return response.data;
};

// Аналитика по гостям программы лояльности iiko.biz: period = 7 | 30 | 365
// или { from, to }. available=false — данных программы нет, аналитика
// остаётся по картам.
export const getCrmProgramAnalytics = async (period) => {
  const response = await api.get('/crm/analytics/program', { params: periodParams(period) });
  return response.data;
};

export const setCrmGuestExcluded = async (guestId, excluded) => {
  const response = await api.put(`/crm/guests/${guestId}/excluded`, { excluded });
  return response.data;
};

export const deleteCrmGuest = async (guestId) => {
  const response = await api.delete(`/crm/guests/${guestId}`);
  return response.data;
};

export const deleteCrmTag = async (tag) => {
  const response = await api.delete('/crm/tags', { data: { tag } });
  return response.data;
};

export const updateCrmGuestProfile = async (guestId, data) => {
  const response = await api.put(`/crm/guests/${guestId}/profile`, data);
  return response.data;
};

export const updateCrmGuestContact = async (guestId, data) => {
  const response = await api.put(`/crm/guests/${guestId}/contact`, data);
  return response.data;
};

// === CRM: акции на сегмент ===
export const getCrmPromotions = async () => {
  const response = await api.get('/crm/promotions');
  return response.data;
};

export const createCrmPromotion = async (data) => {
  const response = await api.post('/crm/promotions', data);
  return response.data;
};

export const getCrmPromotion = async (promotionId) => {
  const response = await api.get(`/crm/promotions/${promotionId}`);
  return response.data;
};

export const previewCrmPromotion = async (filters = {}) => {
  const response = await api.get('/crm/promotions/preview', { params: filters });
  return response.data;
};

export const getCrmPromotionWalletOptions = async (filters = {}) => {
  const response = await api.get('/crm/promotions/wallet-options', { params: filters });
  return response.data;
};

export const getCrmPromotionStatus = async () => {
  const response = await api.get('/crm/promotions/status');
  return response.data;
};

export const runCrmPromotion = async (promotionId) => {
  const response = await api.post(`/crm/promotions/${promotionId}/run`);
  return response.data;
};

export const undoCrmPromotion = async (promotionId) => {
  const response = await api.post(`/crm/promotions/${promotionId}/undo`);
  return response.data;
};

// === CRM: подарочные сертификаты ===
export const getGiftCertificatesConfig = async () => {
  const response = await api.get('/crm/certificates/config');
  return response.data;
};

export const getGiftCertificates = async (params = {}) => {
  const response = await api.get('/crm/certificates', { params });
  return response.data;
};

export const issueGiftCertificate = async (data) => {
  const response = await api.post('/crm/certificates', data);
  return response.data;
};

export const retryGiftCertificate = async (certId) => {
  const response = await api.post(`/crm/certificates/${certId}/retry`);
  return response.data;
};

export const sendGiftCertificate = async (certId, email) => {
  const response = await api.post(`/crm/certificates/${certId}/send`, { email });
  return response.data;
};

export const refreshGiftCertificates = async () => {
  const response = await api.post('/crm/certificates/refresh');
  return response.data;
};

export const giftCertificatePdfUrl = (certId) => `/api/crm/certificates/${certId}/pdf`;

// === CRM: персональные предложения ===
export const getCrmOffers = async () => {
  const response = await api.get('/crm/offers');
  return response.data;
};

export const previewCrmOffers = async (filters = {}) => {
  const response = await api.get('/crm/offers/preview', { params: filters });
  return response.data;
};

export const createCrmOffer = async (data) => {
  const response = await api.post('/crm/offers', data);
  return response.data;
};

export const getCrmOffer = async (offerId) => {
  const response = await api.get(`/crm/offers/${offerId}`);
  return response.data;
};

export const updateCrmOfferItem = async (offerId, itemId, data) => {
  const response = await api.put(`/crm/offers/${offerId}/items/${itemId}`, data);
  return response.data;
};

export const getCrmOfferWalletOptions = async (offerId) => {
  const response = await api.get(`/crm/offers/${offerId}/wallet-options`);
  return response.data;
};

export const getCrmOfferStatus = async () => {
  const response = await api.get('/crm/offers/status');
  return response.data;
};

export const runCrmOffer = async (offerId, walletName) => {
  const response = await api.post(`/crm/offers/${offerId}/run`, { wallet_name: walletName });
  return response.data;
};

export const undoCrmOffer = async (offerId) => {
  const response = await api.post(`/crm/offers/${offerId}/undo`);
  return response.data;
};

// Профиль
export const getProfile = async () => {
  const response = await api.get('/profile');
  return response.data;
};

export const updateProfile = async (data) => {
  const response = await api.put('/profile', data);
  return response.data;
};

export const updateAvatar = async (avatar) => {
  const response = await api.post('/profile/avatar', { avatar });
  return response.data;
};

export const deleteAvatar = async () => {
  const response = await api.delete('/profile/avatar');
  return response.data;
};

// Смена пароля идёт отдельным запросом: нужен текущий пароль
export const changePassword = async (currentPassword, newPassword) => {
  const response = await api.post('/profile/password', {
    current_password: currentPassword,
    new_password: newPassword
  });
  return response.data;
};

// Данные дашборда
export const getRevenue = async () => {
  const response = await api.get('/revenue');
  return response.data;
};

export const getRevenueTrend = async (granularity = 'day') => {
  const response = await api.get('/revenue/trend', { params: { granularity } });
  return response.data;
};

export const refreshData = async () => {
  const response = await api.get('/refresh');
  return response.data;
};

// «Онлайн»: живой зал
export const getOnlineStatus = async () => {
  const response = await api.get('/online');
  return response.data;
};

// Кухонный экран
export const getKitchenScreen = async () => {
  const response = await api.get('/kitchen-screen');
  return response.data;
};

// Списания
export const getWriteoffs = async (period) => {
  const response = await api.get(`/writeoffs?period=${period}`);
  return response.data;
};

export const getWriteoffsCustom = async (from, to) => {
  const response = await api.get(`/writeoffs/custom?from=${from}&to=${to}`);
  return response.data;
};

// Кухня
export const getKitchenAnalysis = async ({ period, from, to } = {}) => {
  const params = new URLSearchParams();
  if (from && to) {
    params.set('from', from);
    params.set('to', to);
  } else if (period) {
    params.set('period', period);
  }
  const query = params.toString();
  const response = await api.get(`/kitchen/analysis${query ? `?${query}` : ''}`);
  return response.data;
};

export const refreshKitchen = async () => {
  const response = await api.get('/kitchen/refresh');
  return response.data;
};

export const getKitchenCategories = async () => {
  const response = await api.get('/kitchen/categories');
  return response.data;
};

export const saveKitchenCategories = async (categories) => {
  const response = await api.post('/kitchen/categories', { categories });
  return response.data;
};

export const searchDish = async (query) => {
  const response = await api.get(`/kitchen/search?q=${encodeURIComponent(query)}`);
  return response.data;
};

// Касса
export const getCashShifts = async () => {
  const response = await api.get('/cash');
  return response.data;
};

export const refreshCash = async () => {
  const response = await api.get('/cash/refresh');
  return response.data;
};

// Чеки
export const getReceipts = async (date) => {
  const response = await api.get('/receipts', { params: { date } });
  return response.data;
};

// Сотрудники
export const getEmployees = async () => {
  const response = await api.get('/employees');
  return response.data;
};

export const refreshEmployees = async () => {
  const response = await api.get('/employees/refresh');
  return response.data;
};

// KPI
// role необязателен (без него отдаются все роли) — раньше при вызове без
// роли в URL буквально уходило "role=undefined", и бэкенд фильтровал по
// несуществующей роли "undefined", возвращая пустые критерии.
export const getKPICriteria = async (role, flat = false) => {
  const params = new URLSearchParams();
  if (role) params.set('role', role);
  if (flat) params.set('flat', 'true');
  const qs = params.toString();
  const response = await api.get(`/kpi/criteria${qs ? `?${qs}` : ''}`);
  return response.data;
};

export const saveKPICriteria = async (role, criteria) => {
  const response = await api.post('/kpi/criteria', { role, criteria });
  return response.data;
};

export const getKPIEmployees = async (role) => {
  const response = await api.get(`/kpi/employees${role ? `?role=${role}` : ''}`);
  return response.data;
};

export const saveKPIEmployees = async (role, employees) => {
  const response = await api.post('/kpi/employees', { role, employees });
  return response.data;
};

export const deleteKPIEmployee = async (employeeId) => {
  const response = await api.delete(`/kpi/employees/${employeeId}`);
  return response.data;
};

export const saveKPIHistory = async (data) => {
  const response = await api.post('/kpi/history', data);
  return response.data;
};

export const getKPIHistory = async (params) => {
  const response = await api.get('/kpi/history', { params });
  return response.data;
};

// Пользователи (админ)
export const getUsers = async () => {
  const response = await api.get('/users');
  return response.data;
};

export const createUser = async (userData) => {
  const response = await api.post('/users', userData);
  return response.data;
};

export const updateUser = async (userId, userData) => {
  const response = await api.put(`/users/${userId}`, userData);
  return response.data;
};

export const deleteUser = async (userId) => {
  const response = await api.delete(`/users/${userId}`);
  return response.data;
};

// Роли организации и доступ к страницам
export const getRoles = async () => {
  const response = await api.get('/roles');
  return response.data;
};

export const createRole = async (data) => {
  const response = await api.post('/roles', data);
  return response.data;
};

export const updateRole = async (roleId, data) => {
  const response = await api.put(`/roles/${roleId}`, data);
  return response.data;
};

export const deleteRole = async (roleId) => {
  const response = await api.delete(`/roles/${roleId}`);
  return response.data;
};

export const getAuditLog = async () => {
  const response = await api.get('/audit-log');
  return response.data;
};

// Месячные данные
export const getMonthlyRevenue = async (year, month) => {
  const response = await api.get(`/revenue/monthly?year=${year}&month=${month}`);
  return response.data;
};

export const getMonthlyWaiters = async (year, month) => {
  const response = await api.get(`/waiters/monthly?year=${year}&month=${month}`);
  return response.data;
};

export const getMonthlyWriteoffs = async (year, month) => {
  const response = await api.get(`/writeoffs/monthly?year=${year}&month=${month}`);
  return response.data;
};

export const getKPIIikoFacts = async (year, month) => {
  const response = await api.get(`/kpi/iiko-facts?year=${year}&month=${month}`);
  return response.data;
};

export const getKPIBonusScale = async () => {
  const response = await api.get('/kpi/bonus-scale');
  return response.data;
};

// KPI по месяцам: у каждого месяца свои цели, факт и итоги (см. kpi.py).
export const getKPIMonth = async (year, month) => {
  const response = await api.get('/kpi/month', { params: { year, month } });
  return response.data;
};

export const saveKPIGoals = async (year, month, role, metrics, bonus) => {
  const response = await api.put('/kpi/month/goals', { year, month, role, metrics, bonus });
  return response.data;
};

export const setKPIValue = async (year, month, role, employeeId, key, value) => {
  const response = await api.put('/kpi/month/values', {
    year, month, role, employee_id: employeeId || 0, key, value
  });
  return response.data;
};

export const loadKPIFromIiko = async (year, month) => {
  const response = await api.post('/kpi/month/load-iiko', { year, month });
  return response.data;
};

export const closeKPIMonth = async (year, month, force = false) => {
  const response = await api.post('/kpi/month/close', { year, month, force });
  return response.data;
};

export const reopenKPIMonth = async (year, month) => {
  const response = await api.post('/kpi/month/reopen', { year, month });
  return response.data;
};

export const getKPIPayrollMatches = async (year, month) => {
  const response = await api.get('/kpi/month/payroll-matches', { params: { year, month } });
  return response.data;
};

export const pushKPIToPayroll = async (year, month, mapping) => {
  const response = await api.post('/kpi/month/push-payroll', { year, month, mapping });
  return response.data;
};

export const getKPIAnalytics = async (months = 12) => {
  const response = await api.get('/kpi/analytics', { params: { months } });
  return response.data;
};

// === Сотрудники: справочник и табель ===
export const getHrEmployees = async (params = {}) => {
  const query = new URLSearchParams(params).toString();
  const response = await api.get(`/hr/employees${query ? `?${query}` : ''}`);
  return response.data;
};

export const getHrEmployee = async (employeeId) => {
  const response = await api.get(`/hr/employees/${employeeId}`);
  return response.data;
};

export const createHrEmployee = async (data) => {
  const response = await api.post('/hr/employees', data);
  return response.data;
};

export const updateHrEmployee = async (employeeId, data) => {
  const response = await api.patch(`/hr/employees/${employeeId}`, data);
  return response.data;
};

export const deleteHrEmployee = async (employeeId) => {
  const response = await api.delete(`/hr/employees/${employeeId}`);
  return response.data;
};

export const importHrEmployeesFromIiko = async () => {
  const response = await api.post('/hr/iiko/employees/import');
  return response.data;
};

export const getHrPeriods = async () => {
  const response = await api.get('/hr/periods');
  return response.data;
};

export const createHrPeriod = async (year, month) => {
  const response = await api.post('/hr/periods', { year, month });
  return response.data;
};

export const getHrPeriod = async (periodId) => {
  const response = await api.get(`/hr/periods/${periodId}`);
  return response.data;
};

export const syncHrPeriod = async (periodId) => {
  const response = await api.post(`/hr/periods/${periodId}/sync`);
  return response.data;
};

export const setHrPeriodStatus = async (periodId, status) => {
  const response = await api.post(`/hr/periods/${periodId}/status`, { status });
  return response.data;
};

export const fillHrHoursFromIiko = async (periodId, overwrite = false) => {
  const response = await api.post(`/hr/iiko/periods/${periodId}/hours`, { overwrite });
  return response.data;
};

export const updateHrEntry = async (entryId, data) => {
  const response = await api.patch(`/hr/entries/${entryId}`, data);
  return response.data;
};

export const setHrEntryDay = async (entryId, day, hours, dayType = 'work') => {
  const response = await api.patch(`/hr/entries/${entryId}/days/${day}`, { hours, day_type: dayType });
  return response.data;
};

export const clearHrEntryDays = async (entryId) => {
  const response = await api.post(`/hr/entries/${entryId}/days/clear`);
  return response.data;
};

export const resetHrEntryTotal = async (entryId, half = null) => {
  const response = await api.post(`/hr/entries/${entryId}/reset-total`, half ? { half } : {});
  return response.data;
};

export const importHrPayrollRows = async (rows) => {
  const response = await api.post('/hr/import', { rows });
  return response.data;
};

// Табель: пакетная запись клеток (одна клетка, заливка диапазона, вставка из Excel)
export const saveHrDays = async (periodId, items) => {
  const response = await api.post(`/hr/periods/${periodId}/days`, { items });
  return response.data;
};

export const setHrClosedDay = async (periodId, day, note, closed = true) => {
  const response = await api.post(`/hr/periods/${periodId}/closed-days`, { day, note, closed });
  return response.data;
};

export const addHrPeriodEntry = async (periodId, data) => {
  const response = await api.post(`/hr/periods/${periodId}/entries`, data);
  return response.data;
};

export const deleteHrEntry = async (entryId) => {
  const response = await api.delete(`/hr/entries/${entryId}`);
  return response.data;
};

export const importHrTimesheetXlsx = async (file, dryRun) => {
  const form = new FormData();
  form.append('file', file);
  const response = await api.post(`/hr/import/xlsx${dryRun ? '?dry_run=1' : ''}`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120000,
  });
  return response.data;
};

export const getHrSummary = async (periodId) => {
  const response = await api.get(`/hr/summary${periodId ? `?period_id=${periodId}` : ''}`);
  return response.data;
};

export const payrollExportUrl = (periodId) => `/api/hr/periods/${periodId}/export`;

export const getHrPayslip = async (entryId) => {
  const response = await api.get(`/hr/entries/${entryId}/payslip`);
  return response.data;
};

export const saveHrPosition = async (data) => {
  const response = await api.post('/hr/positions', data);
  return response.data;
};

export const deleteHrPosition = async (positionId) => {
  const response = await api.delete(`/hr/positions/${positionId}`);
  return response.data;
};

export const getHrMonthRevenue = async (year, month) => {
  const response = await api.get(`/hr/iiko/revenue?year=${year}&month=${month}`);
  return response.data;
};

// === Расписание смен на неделю ===
export const getHrSchedule = async (week) => {
  const response = await api.get(`/hr/schedule${week ? `?week=${week}` : ''}`);
  return response.data;
};

export const saveHrShift = async (data) => {
  const response = await api.post('/hr/schedule', data);
  return response.data;
};

export const deleteHrShift = async (shiftId) => {
  const response = await api.delete(`/hr/schedule/${shiftId}`);
  return response.data;
};

export const copyHrScheduleWeek = async (toWeek, fromWeek, replace = false) => {
  const response = await api.post('/hr/schedule/copy', { to_week: toWeek, from_week: fromWeek, replace });
  return response.data;
};

export const scheduleToPayroll = async (week, overwrite = false) => {
  const response = await api.post('/hr/schedule/to-payroll', { week, overwrite });
  return response.data;
};

// ФОТ за текущий месяц — виджет на дашборде
export const getCurrentMonthPayroll = async () => {
  const response = await api.get('/payroll/current-month');
  return response.data;
};

// === API для мессенджера ===
export const getChats = async () => {
  const response = await api.get('/messages/chats');
  return response.data;
};

export const getAvailableUsers = async () => {
  const response = await api.get('/messages/users');
  return response.data;
};

export const getMessages = async (chatId) => {
  const response = await api.get(`/messages/${chatId}`);
  return response.data;
};

export const sendMessage = async (chatId, message, replyTo = null) => {
  const response = await api.post(`/messages/${chatId}`, { message, reply_to: replyTo });
  return response.data;
};

export const createChat = async (userId) => {
  const response = await api.post('/messages/chat', { user_id: userId });
  return response.data;
};

export const pingPresence = async () => {
  const response = await api.post('/presence/ping');
  return response.data;
};

export const markChatRead = async (chatId) => {
  const response = await api.post(`/messages/${chatId}/read`);
  return response.data;
};

export const getMessageAttachment = async (messageId) => {
  const response = await api.get(`/messages/attachment/${messageId}`);
  return response.data;
};

// === РАСШИРЕННЫЕ API ДЛЯ МЕССЕНДЖЕРА ===
export const editMessage = async (messageId, message) => {
  const response = await api.put(`/messages/${messageId}/edit`, { message });
  return response.data;
};

export const deleteMessage = async (messageId, forEveryone = true) => {
  const response = await api.delete(`/messages/${messageId}/delete`, { data: { for_everyone: forEveryone } });
  return response.data;
};

export const uploadAttachment = async (chatId, message, attachment, attachmentType, attachmentName) => {
  const response = await api.post('/messages/upload', {
    chat_id: chatId,
    message,
    attachment,
    attachment_type: attachmentType,
    attachment_name: attachmentName
  });
  return response.data;
};

export const searchMessages = async (query, chatId = 'all') => {
  const response = await api.get(`/messages/search?q=${encodeURIComponent(query)}&chat_id=${chatId}`);
  return response.data;
};

export const createGroupChat = async (name, userIds, description = '') => {
  const response = await api.post('/messages/group', { name, user_ids: userIds, description });
  return response.data;
};

export const addToGroup = async (chatId, userId) => {
  const response = await api.post(`/messages/group/${chatId}/add`, { user_id: userId });
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await api.get('/messages/unread/count');
  return response.data;
};

// === AI ПРОГНОЗ ===
export const getDailyForecast = async () => {
  const response = await api.get('/forecast/daily');
  return response.data;
};

export const getMonthlyForecast = async () => {
  const response = await api.get('/forecast/monthly');
  return response.data;
};

export const getRecommendations = async () => {
  const response = await api.get('/forecast/recommendations');
  return response.data;
};

export const getAdvisorInsights = async () => {
  const response = await api.get('/advisor/insights');
  return response.data;
};

export const getForecastHistoryStatus = async () => {
  const response = await api.get('/forecast/history-status');
  return response.data;
};

export const refreshForecastHistory = async () => {
  const response = await api.post('/forecast/refresh-history');
  return response.data;
};

// === ЗАКУПКИ ===
export const getPurchases = async (params = {}) => {
  const response = await api.get('/purchases', { params });
  return response.data;
};

export const createPurchase = async (data) => {
  const response = await api.post('/purchases', data);
  return response.data;
};

export const updatePurchase = async (purchaseId, data) => {
  const response = await api.put(`/purchases/${purchaseId}`, data);
  return response.data;
};

export const deletePurchase = async (purchaseId) => {
  const response = await api.delete(`/purchases/${purchaseId}`);
  return response.data;
};

export const decidePurchase = async (purchaseId, decision, comment = '') => {
  const response = await api.post(`/purchases/${purchaseId}/decision`, { decision, comment });
  return response.data;
};

export const markPurchaseBought = async (purchaseId, data) => {
  const response = await api.post(`/purchases/${purchaseId}/purchased`, data);
  return response.data;
};

// История закупки: кто, что и когда с ней делал
export const getPurchaseEvents = async (purchaseId) => {
  const response = await api.get(`/purchases/${purchaseId}/events`);
  return response.data;
};

// Карта согласований: кто согласует закупки каждого сотрудника
export const getApprovalMap = async () => {
  const response = await api.get('/purchases/approval-map');
  return response.data;
};

export const setApprover = async (userId, approverId) => {
  const response = await api.post('/purchases/approval-map', {
    user_id: userId,
    approver_id: approverId,
  });
  return response.data;
};

// === УВЕДОМЛЕНИЯ ===
export const getNotifications = async (params = {}) => {
  const response = await api.get('/notifications', { params });
  return response.data;
};

// Лёгкий запрос для счётчика на колокольчике
export const getNotificationsCount = async () => {
  const response = await api.get('/notifications/count');
  return response.data;
};

export const markNotificationRead = async (notificationId) => {
  const response = await api.post(`/notifications/${notificationId}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.post('/notifications/read-all');
  return response.data;
};

// === PUSH-УВЕДОМЛЕНИЯ (браузерный Push API — см. services/push.js) ===
export const getVapidPublicKey = async () => {
  const response = await api.get('/push/vapid-public-key');
  return response.data;
};

export const subscribePush = async (subscription) => {
  const response = await api.post('/push/subscribe', subscription);
  return response.data;
};

export const unsubscribePush = async (endpoint) => {
  const response = await api.post('/push/unsubscribe', { endpoint });
  return response.data;
};

// === СВОДКА В TELEGRAM (личная привязка сотрудника к боту организации) ===
export const getMyTelegram = async () => {
  const response = await api.get('/me/telegram');
  return response.data;
};

// Возвращает { url } — deep-ссылку t.me/<бот>?start=staff_<код>, код живёт 30 минут
export const createMyTelegramLink = async () => {
  const response = await api.post('/me/telegram/link');
  return response.data;
};

export const updateMyTelegram = async (settings) => {
  const response = await api.put('/me/telegram', settings);
  return response.data;
};

export const unlinkMyTelegram = async () => {
  const response = await api.delete('/me/telegram');
  return response.data;
};

export const sendMyTestDigest = async () => {
  const response = await api.post('/me/telegram/test');
  return response.data;
};

export const getTodaySchedule = async () => {
  const response = await api.get('/hr/schedule/today');
  return response.data;
};

// === ОТЧЁТ О ВЫРУЧКЕ ЗА ПЕРИОД ===
export const getRevenueReport = async (from, to) => {
  const response = await api.get('/revenue/report', { params: { from, to } });
  return response.data;
};

// Прямая ссылка для скачивания: браузер сам отправит cookie сессии.
export const revenueReportCsvUrl = (from, to) =>
  `/api/revenue/report.csv?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;

// === ЦЕЛЬ ПО ВЫРУЧКЕ НА МЕСЯЦ ===
export const getRevenueGoal = async (month) => {
  const response = await api.get('/revenue/goal', { params: month ? { month } : {} });
  return response.data;
};

export const setRevenueGoal = async (month, amount) => {
  const response = await api.put('/revenue/goal', { month, amount });
  return response.data;
};

export default api;
// --- Раскладка дашборда (порядок, ширина и скрытые виджеты) ---
// layout: null — стандартная раскладка, иначе [{ id, size, hidden }]
export const getDashboardLayout = async () => {
  const response = await api.get('/me/dashboard-layout');
  return response.data;
};

export const saveDashboardLayout = async (layout) => {
  const response = await api.put('/me/dashboard-layout', { layout });
  return response.data;
};

export const resetDashboardLayout = async () => {
  const response = await api.delete('/me/dashboard-layout');
  return response.data;
};

// --- Рассылки гостям ---
export const getCrmCampaigns = async () => (await api.get('/crm/campaigns')).data;
export const previewCrmCampaign = async (data) => (await api.post('/crm/campaigns/preview', data)).data;
export const createCrmCampaign = async (data) => (await api.post('/crm/campaigns', data)).data;
export const getCrmCampaign = async (id, status) => (await api.get(`/crm/campaigns/${id}`, { params: status ? { status } : {} })).data;
export const updateCrmCampaign = async (id, data) => (await api.put(`/crm/campaigns/${id}`, data)).data;
export const deleteCrmCampaign = async (id) => (await api.delete(`/crm/campaigns/${id}`)).data;
export const uploadCrmCampaignImage = async (id, image) => (await api.put(`/crm/campaigns/${id}/image`, { image })).data;
export const deleteCrmCampaignImage = async (id) => (await api.delete(`/crm/campaigns/${id}/image`)).data;
export const testCrmCampaign = async (id, phone) => (await api.post(`/crm/campaigns/${id}/test`, { phone })).data;
export const runCrmCampaign = async (id) => (await api.post(`/crm/campaigns/${id}/run`)).data;
export const scheduleCrmCampaign = async (id, scheduledAt) => (await api.post(`/crm/campaigns/${id}/schedule`, { scheduled_at: scheduledAt })).data;
export const cancelCrmCampaign = async (id) => (await api.post(`/crm/campaigns/${id}/cancel`)).data;
export const getCrmSubscribers = async (params) => (await api.get('/crm/subscribers', { params })).data;
export const getCrmSubscribeLinks = async (source) => (await api.get('/crm/subscribe-links', { params: source ? { source } : {} })).data;
export const getOrgMessengers = async () => (await api.get('/org/messengers')).data;
export const updateOrgMaxBot = async (botToken) => (await api.put('/org/max-bot', { bot_token: botToken })).data;
export const updateOrgVkBot = async (token) => (await api.put('/org/vk-bot', { token })).data;
export const updateOrgMailingRules = async (rules) => (await api.put('/org/mailing-rules', rules)).data;
export const getOrgBotProfile = async () => (await api.get('/org/bot-profile')).data;
export const updateOrgBotProfile = async (data) => (await api.put('/org/bot-profile', data)).data;
export const applyOrgBotProfile = async () => (await api.post('/org/bot-profile/apply')).data;
export const getBotPromos = async () => (await api.get('/crm/bot-promos')).data;
export const createBotPromo = async (data) => (await api.post('/crm/bot-promos', data)).data;
export const updateBotPromo = async (id, data) => (await api.put(`/crm/bot-promos/${id}`, data)).data;
export const deleteBotPromo = async (id) => (await api.delete(`/crm/bot-promos/${id}`)).data;
export const getOrgGuestRegistration = async () => (await api.get('/org/guest-registration')).data;
export const updateOrgGuestRegistration = async (data) => (await api.put('/org/guest-registration', data)).data;
export const getOrgLoyaltyPrograms = async () => (await api.get('/org/loyalty-programs')).data;
export const getOrgWelcomeBonus = async () => (await api.get('/org/welcome-bonus')).data;
export const updateOrgWelcomeBonus = async (data) => (await api.put('/org/welcome-bonus', data)).data;
export const getOrgWelcomeBonusWallets = async () => (await api.get('/org/welcome-bonus/wallets')).data;
